// src/common/results-cache.js
import { renderAuditResults, renderInitialCitationPlaceholders, updateHeaderStatus } from './ui.js';
import { calculateCitationScore, quoteIssueLabel } from './ui/badges-status.js';
import { refreshHeadingStatsForFootnote } from './ui/heading-groups.js';
import { summarizeResults } from './result-summary.js';

/**
 * Manages storage and rendering of audit results for footnotes.
 */
export class ResultsCache {
  constructor() {
    this.cache = new Map();
    this.jointResults = new Map();
    this.footnoteListEl = null;
    this.fileCatalogManager = null;
    this.reassignCitationFileFn = null;
    this.resetCitationFn = null;
    this.items = []; // Array of footnote items for stats refresh
  }

  setFootnoteListEl(element) {
    this.footnoteListEl = element;
  }

  setFileCatalogManager(fileCatalogManager) {
    this.fileCatalogManager = fileCatalogManager;
  }

  setReassignCitationFileFn(fn) {
    this.reassignCitationFileFn = fn;
  }

  setResetCitationFn(fn) {
    this.resetCitationFn = fn;
  }

  setItems(items) {
    this.items = items || [];
  }

  /**
   * Clone results payload safely (supports structuredClone or JSON fallback).
   */
  cloneResults(payload) {
    try {
      if (typeof structuredClone === 'function') return structuredClone(payload);
    } catch (err) {
      console.warn('structuredClone failed, falling back to JSON clone:', err);
    }
    try {
      return JSON.parse(JSON.stringify(payload));
    } catch {
      return payload;
    }
  }

  /**
   * Get render options for a footnote's results.
   */
  getRenderOptions(footnoteId) {
    return {
      jointResults: this.jointResults.get(String(footnoteId)) || {},
      resolveFileDisplay: (entry) => this.fileCatalogManager?.getFileDisplay(entry) || { fileId: null, label: '—', info: null },
      getFileSourceUrl: (fileId, sourceDb) => this.fileCatalogManager?.getFileSourceUrl(fileId, sourceDb) || null,
      onChangeFile: (citeId) => this.reassignCitationFileFn?.(footnoteId, citeId),
      onResetCitation: (citeId, interaction) => this.resetCitationFn?.(footnoteId, citeId, interaction),
    };
  }

  /**
   * Store results for a footnote without rendering (useful when cards don't exist yet).
   */
  storeResults(footnoteId, payload) {
    const key = String(footnoteId);
    const clone = this.cloneResults(payload || {});
    this.cache.set(key, clone);
    this.hydrateJointResults(key, clone);
  }

  hydrateJointResults(footnoteId, results) {
    const groups = { ...this.getJointResults(footnoteId) };
    for (const entry of Object.values(results || {})) {
      const group = entry?.joint_group_result || entry?.congruence?.joint_group_result
        || entry?.congruence_result?.joint_group_result;
      if (group?.group_id) groups[group.group_id] = group;
    }
    if (Object.keys(groups).length) this.jointResults.set(String(footnoteId), groups);
  }

  storeJointResults(footnoteId, payload) {
    this.jointResults.set(String(footnoteId), this.cloneResults(payload || {}));
  }

  getJointResults(footnoteId) {
    return this.jointResults.get(String(footnoteId)) || {};
  }

  clearJointGroupFromEntries(results, groupId) {
    for (const entry of Object.values(results || {})) {
      if (entry?.joint_evaluation_group !== groupId) continue;
      delete entry.joint_group_result;
      if (entry.congruence) delete entry.congruence.joint_group_result;
      if (entry.congruence_result) delete entry.congruence_result.joint_group_result;
    }
  }

  invalidateJointGroup(footnoteId, groupId) {
    if (!groupId) return;
    const key = String(footnoteId);
    const groups = { ...this.getJointResults(key) };
    delete groups[groupId];
    this.jointResults.set(key, groups);
    const results = this.cloneResults(this.cache.get(key) || {});
    this.clearJointGroupFromEntries(results, groupId);
    this.cache.set(key, results);
    if (this.footnoteListEl) renderAuditResults(
      this.footnoteListEl, key, results, this.getRenderOptions(key));
  }

  /**
   * Store and render results for a footnote.
   */
  storeAndRenderResults(footnoteId, payload) {
    const key = String(footnoteId);
    const clone = this.cloneResults(payload || {});
    this.cache.set(key, clone);
    this.hydrateJointResults(key, clone);

    if (this.footnoteListEl) {
      // Proactively clear out any existing citation blocks for this footnote so
      // that "Check Again" never leaves stale congruence results in the UI.
      try {
        const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(key) : key;
        const card = this.footnoteListEl.querySelector(`.card[data-id="${escapedId}"]`);
        const container = card?.querySelector('.results');
        if (container) {
          // Remove only citation blocks; keep other UI (e.g., ask containers)
          container.querySelectorAll('.citation-block').forEach(block => block.remove());
        }
      } catch (err) {
        console.warn('[ResultsCache] Failed to clear previous citation blocks:', err);
      }

      renderAuditResults(this.footnoteListEl, footnoteId, clone, this.getRenderOptions(footnoteId));

      // Refresh heading stats for this footnote's parent heading
      refreshHeadingStatsForFootnote(this.footnoteListEl, footnoteId, this, this.items);

      // Update citation tabs if available (web-app feature)
      // Only update when we have FINAL results with actual labels
      // Use requestAnimationFrame to ensure DOM has updated before cloning
      if (typeof window !== 'undefined' && window.updateCitationTabs) {
        requestAnimationFrame(() => {
          // Check if any citation has a final label (not just intermediate status)
          for (const [citationId, result] of Object.entries(clone)) {
            if (citationId !== '__error__' && result) {
              // Extract the label - check all possible locations
              const label = result.label ||
                            result.analyze_result?.label ||
                            result.analyze_result?.congruence_result?.label ||
                            result.congruence_result?.label;

              // Only update tabs if we have a final label (not empty, not pending states)
              if (label && !['pending', 'processing', 'resolving', 'analyzing'].includes(label.toLowerCase())) {
                window.updateCitationTabs(footnoteId, citationId, result);
              }
            }
          }
        });
      }
    }
  }

  /**
   * Get cached results for a footnote.
   */
  getResults(footnoteId) {
    const key = String(footnoteId);
    return this.cache.get(key) || null;
  }

  /**
   * Check if results exist for a footnote.
   */
  hasResults(footnoteId) {
    const key = String(footnoteId);
    return this.cache.has(key);
  }

  /**
   * Delete cached results for a footnote.
   */
  deleteResults(footnoteId) {
    const key = String(footnoteId);
    this.cache.delete(key);
    this.jointResults.delete(key);
  }

  /**
   * Delete one citation result and re-render parsed placeholders plus remaining results.
   */
  deleteCitationResult(footnoteId, citationId, parsedCitations = []) {
    const key = String(footnoteId);
    const citeKey = String(citationId);
    const current = this.cloneResults(this.cache.get(key) || {});
    const groupId = current[citeKey]?.joint_evaluation_group
      || parsedCitations.find(c => String(c.id) === citeKey)?.joint_evaluation_group;
    if (groupId) {
      const groups = { ...this.getJointResults(key) };
      delete groups[groupId];
      this.jointResults.set(key, groups);
      this.clearJointGroupFromEntries(current, groupId);
    }
    delete current[citeKey];

    if (Object.keys(current).length > 0) {
      this.cache.set(key, current);
    } else {
      this.cache.delete(key);
    }

    if (!this.footnoteListEl) return current;

    try {
      const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(key) : key;
      const card = this.footnoteListEl.querySelector(`.card[data-id="${escapedId}"]`);
      const container = card?.querySelector('.results');
      if (container) {
        if (Array.isArray(parsedCitations) && parsedCitations.length > 0) {
          renderInitialCitationPlaceholders(container, parsedCitations, { statusMessage: null });
          if (Object.keys(current).length > 0) {
            renderAuditResults(this.footnoteListEl, footnoteId, current, this.getRenderOptions(footnoteId));
          }
        } else {
          const escapedCite = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(citeKey) : citeKey;
          const block = container.querySelector(`.citation-block[data-cite-id="${escapedCite}"]`);
          block?.remove();
        }
      }

      updateHeaderStatus(this.footnoteListEl, footnoteId, current, this.getJointResults(footnoteId));
      const verifyBtn = card?.querySelector('.card-body .footnote-actions button');
      if (verifyBtn) {
        verifyBtn.textContent = Object.keys(current).length > 0 ? 'Check Again' : 'Verify';
      }
    } catch (err) {
      console.warn('[ResultsCache] Failed to re-render after citation reset:', err);
    }

    refreshHeadingStatsForFootnote(this.footnoteListEl, footnoteId, this, this.items);
    return current;
  }

  /**
   * Clear all cached results.
   */
  clear() {
    this.cache.clear();
    // Joint groups are keyed by bare footnote id and group ids are "{fn}:joint:{n}",
    // so a group left over from the previous document lands on the same-numbered
    // footnote of the next one.
    this.jointResults.clear();
  }

  /**
   * Get the size of the cache.
   */
  get size() {
    return this.cache.size;
  }

  /**
   * Get all cached results as a plain object.
   * @returns {Object} Object with footnoteId keys and result values
   */
  getAllResults() {
    const results = {};
    for (const [key, value] of this.cache.entries()) {
      results[key] = value;
    }
    return results;
  }

  /**
   * Calculate summary of results by label.
   * @returns {Object} { verified: number, issues: number, unresolved: number }
   */
  calculateSummary() {
    return summarizeResults(this.cache, this.jointResults);
  }

  /**
   * Calculate detailed summary with quality score for all cached results.
   * @returns {Object} { score: number (0-100, or null if no scorable citations), verified: number, issues: number, unresolved: number, total: number }
   */
  calculateDetailedSummary() {
    let totalScore = 0;
    let scorableCitations = 0;
    let verified = 0;
    let issues = 0;
    let unresolved = 0;
    let total = 0;

    for (const [footnoteId, footnoteResults] of this.cache.entries()) {
      if (!footnoteResults || typeof footnoteResults !== 'object') continue;

      for (const [citationId, result] of Object.entries(footnoteResults)) {
        if (citationId === '__error__') continue;
        if (!result) continue;
        if (result.joint_member_result || result.joint_evaluation_group) continue;

        total++;
        const { score, category } = calculateCitationScore(result);

        if (category === 'verified') {
          verified++;
        } else if (category === 'issues') {
          issues++;
        } else {
          // unresolved - excluded from score
          unresolved++;
        }

        // Score inclusion is based on "scorable" outcomes, not category.
        // This allows including congruence outcomes like `insufficient` while excluding
        // pure resolution failures like `wrong_document`.
        if (score !== null) {
          totalScore += score;
          scorableCitations++;
        }
      }
      for (const group of Object.values(this.getJointResults(footnoteId))) {
        total++;
        const { score, category } = calculateCitationScore(group);
        if (category === 'verified') verified++;
        else if (category === 'issues') issues++;
        else unresolved++;
        if (score !== null) { totalScore += score; scorableCitations++; }
      }
    }

    const score = scorableCitations > 0 ? Math.round(totalScore / scorableCitations) : null;
    return { score, verified, issues, unresolved, total };
  }

  /**
   * Calculate detailed summary for a specific subset of footnotes.
   * @param {Array<string|number>} footnoteIds - Array of footnote IDs to include
   * @returns {Object} { score: number (0-100, or null if no scorable citations), verified: number, issues: number, unresolved: number, total: number }
   */
  calculateSummaryForFootnotes(footnoteIds) {
    if (!footnoteIds || footnoteIds.length === 0) {
      return { score: null, verified: 0, issues: 0, unresolved: 0, total: 0 };
    }

    let totalScore = 0;
    let scorableCitations = 0;
    let verified = 0;
    let issues = 0;
    let unresolved = 0;
    let total = 0;

    const idSet = new Set(footnoteIds.map(id => String(id)));

    for (const [footnoteId, footnoteResults] of this.cache.entries()) {
      if (!idSet.has(footnoteId)) continue;
      if (!footnoteResults || typeof footnoteResults !== 'object') continue;

      for (const [citationId, result] of Object.entries(footnoteResults)) {
        if (citationId === '__error__') continue;
        if (!result) continue;
        if (result.joint_member_result || result.joint_evaluation_group) continue;

        total++;
        const { score, category } = calculateCitationScore(result);

        if (category === 'verified') {
          verified++;
        } else if (category === 'issues') {
          issues++;
        } else {
          // unresolved - excluded from score
          unresolved++;
        }

        if (score !== null) {
          totalScore += score;
          scorableCitations++;
        }
      }
      for (const group of Object.values(this.getJointResults(footnoteId))) {
        total++;
        const { score, category } = calculateCitationScore(group);
        if (category === 'verified') verified++;
        else if (category === 'issues') issues++;
        else unresolved++;
        if (score !== null) { totalScore += score; scorableCitations++; }
      }
    }

    const score = scorableCitations > 0 ? Math.round(totalScore / scorableCitations) : null;
    return { score, verified, issues, unresolved, total };
  }
}
